import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, CreditCard, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const features = [
  "Unlimited invoices and customers",
  "Billing, stock and jewellery retail workflows",
  "Secure cloud backup and access control",
  "Setup, onboarding and staff training included",
  "Product guidance and technical support",
  "Regular product improvements and updates",
];

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
});

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4ff] via-[#fdf9f3] to-[#e8f4fc] text-slate-900">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(129,140,248,0.18),transparent),radial-gradient(ellipse_60%_40%_at_100%_100%,rgba(245,158,11,0.12),transparent)]" />
        <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-violet-500/20 blur-3xl" />
      </div>

      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">
        <motion.div initial="hidden" animate="visible" variants={fadeUp()}>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-violet-300 hover:text-violet-700">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </motion.div>

        <motion.section initial="hidden" animate="visible" variants={fadeUp(0.05)} className="mt-6 overflow-hidden rounded-[30px] border border-violet-200/70 bg-white/90 p-6 text-center shadow-xl shadow-violet-200/30 backdrop-blur-sm md:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-700 ring-1 ring-violet-200/70">
            <CreditCard className="h-3.5 w-3.5" /> Pricing
          </div>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">Simple pricing for jewellery retailers</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-[15px]">
            Choose monthly flexibility or save with the yearly plan. Both plans include the complete SwitchIn experience.
          </p>
        </motion.section>

        <section className="mt-6 grid gap-5 md:grid-cols-2">
          <motion.article initial="hidden" animate="visible" variants={fadeUp(0.1)} className="rounded-[28px] border border-slate-200/80 bg-white/95 p-6 shadow-sm">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700"><CalendarDays className="h-5 w-5" /></div>
            <h2 className="mt-4 text-xl font-semibold">Monthly plan</h2>
            <p className="mt-2 text-sm text-slate-600">Flexible month-to-month billing.</p>
            <div className="mt-5 flex items-baseline gap-2"><span className="text-4xl font-semibold">₹3,000</span><span className="text-sm font-medium text-slate-500">/ month</span></div>
          </motion.article>

          <motion.article initial="hidden" animate="visible" variants={fadeUp(0.15)} className="relative overflow-hidden rounded-[28px] border border-violet-300 bg-white/95 p-6 shadow-lg shadow-violet-200/40">
            <div className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200"><Sparkles className="h-3.5 w-3.5" /> Best value</div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700"><CreditCard className="h-5 w-5" /></div>
            <h2 className="mt-4 text-xl font-semibold">Yearly plan</h2>
            <p className="mt-2 text-sm text-slate-600">Best value for established stores.</p>
            <div className="mt-5 flex items-baseline gap-2"><span className="bg-gradient-to-r from-violet-700 to-fuchsia-500 bg-clip-text text-4xl font-semibold text-transparent">₹32,000</span><span className="text-sm font-medium text-slate-500">/ year</span></div>
            <p className="mt-3 text-sm font-semibold text-emerald-700">Save ₹4,000 compared with monthly billing.</p>
          </motion.article>
        </section>

        <motion.section initial="hidden" animate="visible" variants={fadeUp(0.2)} className="mt-6 rounded-[28px] border border-slate-200/80 bg-white/95 p-6 shadow-sm">
          <h2 className="text-xl font-semibold">Everything you need to run your store</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {features.map((feature) => <div key={feature} className="flex items-start gap-2.5 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /><span>{feature}</span></div>)}
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black">Get started <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/#demo" className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 transition hover:border-violet-300 hover:text-violet-700">Book a free demo</Link>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
